import { catchError, map, of, type Observable, concat, zip } from 'rxjs';

import { request } from 'app/utils/request';
import { splitArray } from 'app/utils/array';
import { getHashiHeaders, getHashiUrl, getXTotal } from 'app/utils/api';
import { createCompetitorsRequest } from 'app/pages/MatchPage/ressource';

import type { MonitoringFilter } from './MonitoringFilterContext/types';
import { initialState } from './MonitoringFilterContext/reducer';
import type {
  CompetitorsUpdate,
  MonitoredMarket,
  MonitoringData,
  MonitoringMessage,
  UIEventMessage
} from './types';
import { allMessagesAreDelete } from './utils';

const PAGE_SIZE = 30;

const getMonitoringQuery = (
  {
    comparingWith,
    markets,
    scoreThreshold,
    showHidden,
    selectedTournaments,
    outsideOfMarket
  }: MonitoringFilter,
  page: number,
  countOnly = false
): Record<string, string | string[]> => {
  const queryObject: Record<string, string | string[]> = {
    page: page.toString()
  };

  queryObject['filter[competitor_slug]'] = comparingWith;

  const [marketsWithoutMapHandicap, mapHandicap] = splitArray(markets, market =>
    market.startsWith('map-handicap') ? 1 : 0
  );

  marketsWithoutMapHandicap.forEach((market, index) => {
    const [template, event_type] = market.split('__');
    queryObject[`filter[market][${index}][template]`] = template;
    queryObject[`filter[market][${index}][event_type]`] = event_type;
  });

  if (mapHandicap.length > 0) {
    queryObject[`filter[market][${marketsWithoutMapHandicap.length}][template]`] = 'map-handicap-1';
    queryObject[`filter[market][${marketsWithoutMapHandicap.length}][event_type]`] = 'match';
    queryObject[`filter[market][${marketsWithoutMapHandicap.length + 1}][template]`] =
      'map-handicap-2';
    queryObject[`filter[market][${marketsWithoutMapHandicap.length + 1}][event_type]`] = 'match';
    queryObject[`filter[market][${marketsWithoutMapHandicap.length + 2}][template]`] =
      'map-handicap-3';
    queryObject[`filter[market][${marketsWithoutMapHandicap.length + 2}][event_type]`] = 'match';
  }

  const allVideogamesSelected = selectedTournaments.videogames
    .filter(vg => vg.selected)
    .map(vg => vg.slug);

  const allTournamentsSelected = selectedTournaments.videogames
    .filter(vg => !vg.selected)
    .map(vg => vg.series)
    .flat()
    .map(series => series.tournaments)
    .flat()
    .filter(tournament => tournament.selected)
    .map(tournament => tournament.id.toString());

  if (allVideogamesSelected.length) {
    queryObject['filter[videogame_slug]'] = allVideogamesSelected;
  }
  if (allTournamentsSelected.length) {
    queryObject['filter[tournament_id]'] = allTournamentsSelected;
  }

  queryObject['filter[min_risk_score]'] = scoreThreshold.toFixed(1);

  if (showHidden) {
    queryObject['filter[hidden]'] = 'true';
  }

  if (countOnly) {
    queryObject.total_count_only = 'true';
  }

  if (outsideOfMarket) {
    queryObject['filter[outside_of_market]'] = 'true';
  }

  return queryObject;
};

const monitoringRequest = (query: Record<string, string | string[]>) =>
  request<MonitoredMarket[]>(
    {
      url: getHashiUrl('/risk_score_views/monitoring'),
      headers: getHashiHeaders(),
      query,
      method: 'GET'
    },
    {},
    { arrayFormat: 'bracket' }
  );

function createMonitoringRequest(
  filters: MonitoringFilter,
  page: number
): Observable<Omit<MonitoringData, 'competitorsStatus'>> {
  const query = getMonitoringQuery(filters, page);

  const hiddenNumberQuery = getMonitoringQuery(
    { ...initialState.filters, showHidden: true },
    page,
    true
  );
  const hiddenNumberRequest = filters.showHidden
    ? of(undefined)
    : monitoringRequest(hiddenNumberQuery);

  return zip(monitoringRequest(query), hiddenNumberRequest).pipe(
    map(([response, hiddenNumberResponse]) => {
      const total = getXTotal(response.headers);
      const nbPages = Math.ceil(total / PAGE_SIZE);

      const reachedEnd = page >= nbPages;

      const hiddenMarkets = hiddenNumberResponse
        ? getXTotal(hiddenNumberResponse.headers)
        : undefined;

      return {
        pages: [response.data!],
        currentPage: page,
        reachedEnd,
        loadingPage: false,
        filters,
        hiddenMarkets
      };
    }),
    catchError(() => [])
  );
}

const createFirstMonitoringRequest = (
  filters: MonitoringFilter,
  page: number
): Observable<MonitoringData> =>
  zip(createMonitoringRequest(filters, page), createCompetitorsRequest()).pipe(
    map(([monitoringDataResponse, competitorsResponse]) => ({
      ...monitoringDataResponse,
      competitorsStatus: competitorsResponse
    }))
  );

function handleMonitoringMessage(
  currentData: MonitoringData,
  messages: MonitoringMessage[]
): Observable<MonitoringData> {
  if (messages.some(({ type }) => type === 'init_ressource')) {
    return of(currentData);
  }

  const competitorsMessages = messages.filter(
    ({ type }) => type === 'competitor'
  ) as CompetitorsUpdate[];

  const competitorsUpdated = currentData.competitorsStatus.map(competitor => {
    const updatedCompetitor = { ...competitor };
    competitorsMessages.forEach(message => {
      if (competitor.slug === message.model.slug) {
        updatedCompetitor.status = message.model.status;
      }
    });
    return updatedCompetitor;
  });

  const uiEventMessages = messages.filter(({ type }) => type === 'ui_event') as UIEventMessage[];

  if (uiEventMessages.length) {
    const eventMessage = uiEventMessages.at(-1);
    if (eventMessage) {
      if (eventMessage.action === 'refresh') {
        return createFirstMonitoringRequest(eventMessage.filters, 1);
      }

      // Prevent loading manually the data of the page we're already on
      // Usefull to skip the manual page load of a page that has just been added
      if (eventMessage.action === 'load_page' && eventMessage.page === currentData.currentPage) {
        return of({ ...currentData, competitorsStatus: competitorsUpdated });
      }

      if (eventMessage.action === 'load_next_page' && currentData.reachedEnd) {
        return of({ ...currentData, competitorsStatus: competitorsUpdated });
      }

      const newPage =
        eventMessage.action === 'load_next_page' ? currentData.currentPage + 1 : eventMessage.page;

      const nextPageRequest = createMonitoringRequest(currentData.filters, newPage).pipe(
        map(response => {
          const [markets] = response.pages;
          const marketsRef = markets.map(market => market.market_ref);

          const updatedMarketPages = currentData.pages.map(page =>
            page.filter(market => !marketsRef.includes(market.market_ref))
          );
          updatedMarketPages[newPage - 1] = markets;

          return {
            ...response,
            competitorsStatus: competitorsUpdated,
            pages: updatedMarketPages
          };
        })
      );
      return concat(of({ ...currentData, loadingPage: true }), nextPageRequest);
    }
  }

  if (messages.some(({ type }) => type === 'monitored_market')) {
    if (allMessagesAreDelete(messages)) {
      const allMarketsToDelete = messages.map(message => message.model.market_ref);
      const updatedData = currentData.pages.map(page =>
        page.filter(market => !allMarketsToDelete.includes(market.market_ref))
      );
      return of({
        ...currentData,
        competitorsStatus: competitorsUpdated,
        pages: updatedData
      });
    }

    const currentPageRequest = createMonitoringRequest(
      currentData.filters,
      currentData.currentPage
    ).pipe(
      map(response => {
        const [markets] = response.pages;
        const updatedMarketPages = [...currentData.pages];
        updatedMarketPages[currentData.currentPage - 1] = markets;
        return {
          ...response,
          competitorsStatus: competitorsUpdated,
          pages: updatedMarketPages
        };
      })
    );
    return concat(of({ ...currentData, loadingPage: true }), currentPageRequest);
  }

  return of({ ...currentData, competitorsStatus: competitorsUpdated });
}

export { handleMonitoringMessage, createFirstMonitoringRequest };
